import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, MapPin, Image as ImageIcon, Clock, ArrowRight, ArrowLeft,
  Upload, Loader2, Tag, Ticket, FileText, CheckCircle2, AlertCircle, AlertTriangle,
  Plus, Trash2, Shield, Building2, ExternalLink, Map, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { localDateAndTimeToISO, formatArgentinaLong } from '../../lib/datetime';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import ProducerLayout from '../../components/producer/ProducerLayout';
import VenueSelector from '../../components/venue/VenueSelector';
import VenueTicketEditor from '../../components/venue/VenueTicketEditor';

// Event visibility types (for verified producers only)
const VISIBILITY_TYPES = [
  { value: 'public', label: 'Público', description: 'Visible para todos en la web' },
  { value: 'private', label: 'Privado', description: 'Solo accesible con link directo' },
];

const TICKET_TYPES = [
  { value: 'standard', label: 'Estándar', description: 'Entrada normal' },
  { value: 'invitation', label: 'Invitación', description: 'Entrada gratuita' },
  { value: 'draft', label: 'Borrador', description: 'No visible aún' },
];

const CreateEventPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [producer, setProducer] = useState(null);
  const [producerTerms, setProducerTerms] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  
  // Step 1: Event Info
  const [eventData, setEventData] = useState({
    title: '',
    description: '',
    location: '',
    venue_name: '',
    venue_id: '',  // Link to venue map
    location_url: '',
    visibility: 'public', // public or private
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    image_url: '',
    // Age restriction
    age_restriction_enabled: false,
    minimum_age: 18,
    // Order limits per user
    max_orders_per_user: null,
    custom_terms: '',
    include_terms_in_email: false,
  });
  
  // Selected venue data (full object with elements)
  const [selectedVenue, setSelectedVenue] = useState(null);
  
  // Action type: 'draft', 'publish', 'submit_for_approval'
  const [submitAction, setSubmitAction] = useState('draft');
  
  // Step 2: Tickets
  const [tickets, setTickets] = useState([{
    id: Date.now(),
    name: 'Entrada General',
    type: 'standard',
    stock: 100,
    price: 0,
    max_per_order: 4,
    description: '',
    sale_end_date: '',
    entry_limit_time: '',
    is_visible: true,
    is_active: true,
    discount_code: '',
    discount_percent: 0,
    linked_section_id: '',  // Link to venue section
    linked_section_name: '', // Name for display
    // Tier type & inquiry
    tier_type: 'sale',  // 'sale' | 'inquiry'
    inquiry_link: '',
    inquiry_label: '',
    // Multi-stage pricing (tandas)
    price_stages: []
  }]);

  useEffect(() => {
    loadProducer();
    loadProducerTerms();
  }, []);

  const loadProducer = async () => {
    try {
      const response = await api.get('/producers/me');
      setProducer(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const loadProducerTerms = async () => {
    try {
      const response = await api.get('/settings/producer-terms');
      setProducerTerms(response.data);
    } catch (error) {
      console.error('Error loading terms:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('La imagen no puede superar 10MB');
      return;
    }

    setUploading(true);
    try {
      const sigResponse = await api.get('/cloudinary/signature?folder=producer_events');
      const { signature, timestamp, cloud_name, api_key, folder } = sigResponse.data;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', api_key);
      formData.append('timestamp', timestamp);
      formData.append('signature', signature);
      formData.append('folder', folder);

      const uploadResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );
      
      const uploadData = await uploadResponse.json();
      
      if (uploadData.secure_url) {
        setEventData(prev => ({ ...prev, image_url: uploadData.secure_url }));
        toast.success('Imagen subida');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al subir imagen');
    } finally {
      setUploading(false);
    }
  };

  const addTicket = () => {
    setTickets(prev => [...prev, {
      id: Date.now(),
      name: '',
      type: 'standard',
      stock: 100,
      price: 0,
      max_per_order: 4,
      description: '',
      sale_end_date: '',
      entry_limit_time: '',
      is_visible: true,
      is_active: true,
      discount_code: '',
      discount_percent: 0,
      linked_section_id: '',
      linked_section_name: '',
      tier_type: 'sale',
      inquiry_link: '',
      inquiry_label: '',
      price_stages: []
    }]);
  };

  const removeTicket = (id) => {
    if (tickets.length <= 1) {
      toast.error('Debe haber al menos un tipo de entrada');
      return;
    }
    setTickets(prev => prev.filter(t => t.id !== id));
  };

  const updateTicket = (id, field, value) => {
    setTickets(prev => prev.map(t => 
      t.id === id ? { ...t, [field]: value } : t
    ));
  };

  const validateStep1 = () => {
    if (!eventData.title.trim()) {
      toast.error('Ingresa el nombre del evento');
      return false;
    }
    if (!eventData.location.trim()) {
      toast.error('Ingresa la ubicación');
      return false;
    }
    if (!eventData.start_date || !eventData.start_time) {
      toast.error('Ingresa la fecha y hora de inicio');
      return false;
    }
    if (eventData.age_restriction_enabled && (!eventData.minimum_age || eventData.minimum_age < 1)) {
      toast.error('Ingresa una edad mínima válida');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    // Step 2 is Venue - no validation needed, it's optional
    return true;
  };
  
  const validateStep3 = () => {
    for (const ticket of tickets) {
      if (!ticket.name.trim()) {
        toast.error('Todos los tickets deben tener nombre');
        return false;
      }
      if (ticket.stock < 1) {
        toast.error('El stock debe ser mayor a 0');
        return false;
      }
    }
    if (!termsAccepted) {
      toast.error('Debes aceptar los términos y condiciones');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };
  
  const handleSkipVenue = () => {
    // Clear venue_id and go to tickets
    setEventData(prev => ({ ...prev, venue_id: '' }));
    setStep(3);
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async (action) => {
    if (!validateStep3()) return;
    
    // Validate action based on verification status
    if (action === 'publish' && !producer?.is_verified) {
      toast.error('Solo productoras verificadas pueden publicar directamente');
      return;
    }
    
    setSaving(true);
    try {
      // Use the dedicated helper to interpret inputs as LOCAL time and emit UTC ISO.
      // Avoids the timezone drift bug where 22:00 local could be saved as 22:00 UTC
      // and later rendered back as 22:00 - tz_offset.
      const startIso = localDateAndTimeToISO(eventData.start_date, eventData.start_time);
      const endIso = localDateAndTimeToISO(eventData.end_date, eventData.end_time);
      
      // Determine status based on action
      let status;
      if (action === 'draft') {
        status = 'draft';
      } else if (action === 'publish') {
        status = 'published';
      } else { // submit_for_approval
        status = 'pending_approval';
      }
      
      const payload = {
        title: eventData.title,
        description: eventData.description,
        location: eventData.location,
        venue_name: eventData.venue_name || null,
        venue_id: eventData.venue_id || null,
        location_url: eventData.location_url || null,
        date: startIso,
        end_date: endIso || null,
        image_url: eventData.image_url || null,
        status: status,
        is_private: eventData.visibility === 'private',
        age_restriction_enabled: eventData.age_restriction_enabled,
        minimum_age: eventData.age_restriction_enabled ? eventData.minimum_age : null,
        max_orders_per_user: eventData.max_orders_per_user || null,
        custom_terms: eventData.custom_terms?.trim() || null,
        include_terms_in_email: !!eventData.include_terms_in_email,
        terms_accepted: termsAccepted,
        tickets: tickets.map(t => ({
          name: t.name,
          type: t.type,
          quantity: t.stock,
          price: t.price,
          max_per_order: t.max_per_order,
          description: t.description || null,
          sale_end_date: t.sale_end_date || null,
          entry_limit_time: t.entry_limit_time || null,
          is_visible: t.is_visible,
          is_active: t.is_active,
          discount_code: t.discount_code || null,
          discount_percent: t.discount_percent || 0,
          tier_type: t.tier_type || 'sale',
          inquiry_link: t.inquiry_link || null,
          inquiry_label: t.inquiry_label || null,
          price_stages: (t.price_stages && t.price_stages.length > 0) ? t.price_stages : null
        }))
      };

      const response = await api.post('/producers/me/events', payload);
      
      if (action === 'draft') {
        toast.success('Evento guardado como borrador');
      } else if (action === 'publish') {
        toast.success('Evento publicado exitosamente');
      } else {
        toast.success('Evento enviado para aprobación');
      }
      
      navigate(`/producer/events/${response.data.id}`);
    } catch (error) {
      console.error('Error:', error);
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else if (Array.isArray(errorDetail)) {
        toast.error(errorDetail[0]?.msg || 'Error de validación');
      } else {
        toast.error('Error al crear evento');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProducerLayout currentPage="events">
      <div className="p-4 lg:p-8 max-w-4xl mx-auto" data-testid="create-event-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-black text-white">Crear Evento</h1>
          <p className="text-zinc-400">
            {step === 1 ? 'Información del evento' : 'Configurar entradas'}
          </p>
          
          {/* Progress */}
          <div className="flex items-center gap-4 mt-6">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-violet-500' : 'text-zinc-500'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 1 ? 'bg-violet-500 text-black' : 'bg-zinc-800'
              }`}>
                1
              </div>
              <span className="hidden sm:inline font-medium">Información</span>
            </div>
            <div className={`flex-1 h-1 rounded ${step >= 2 ? 'bg-violet-500' : 'bg-zinc-800'}`} />
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-violet-500' : 'text-zinc-500'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 2 ? 'bg-violet-500 text-black' : 'bg-zinc-800'
              }`}>
                2
              </div>
              <span className="hidden sm:inline font-medium">Venue</span>
            </div>
            <div className={`flex-1 h-1 rounded ${step >= 3 ? 'bg-violet-500' : 'bg-zinc-800'}`} />
            <div className={`flex items-center gap-2 ${step >= 3 ? 'text-violet-500' : 'text-zinc-500'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 3 ? 'bg-violet-500 text-black' : 'bg-zinc-800'
              }`}>
                3
              </div>
              <span className="hidden sm:inline font-medium">Entradas</span>
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {/* General Info */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <FileText size={20} className="text-violet-500" />
                  Información General
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-zinc-400">Nombre del evento *</Label>
                    <Input
                      value={eventData.title}
                      onChange={(e) => setEventData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Ej: Fiesta de Verano 2025"
                      className="bg-zinc-800 border-zinc-700 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Descripción (opcional)</Label>
                    <Textarea
                      value={eventData.description}
                      onChange={(e) => setEventData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe tu evento..."
                      className="bg-zinc-800 border-zinc-700 mt-1"
                      rows={3}
                    />
                  </div>
                </div>
              </section>

              {/* Location */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <MapPin size={20} className="text-violet-500" />
                  Ubicación
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label className="text-zinc-400">Ubicación exacta *</Label>
                    <Input
                      value={eventData.location}
                      onChange={(e) => setEventData(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="Ej: Av. Corrientes 1234, CABA"
                      className="bg-zinc-800 border-zinc-700 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 flex items-center gap-2">
                      <Building2 size={14} />
                      Nombre del venue (opcional)
                    </Label>
                    <Input
                      value={eventData.venue_name}
                      onChange={(e) => setEventData(prev => ({ ...prev, venue_name: e.target.value }))}
                      placeholder="Ej: Club XYZ, Teatro Gran Rex"
                      className="bg-zinc-800 border-zinc-700 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 flex items-center gap-2">
                      <ExternalLink size={14} />
                      Link de Google Maps (opcional)
                    </Label>
                    <Input
                      value={eventData.location_url}
                      onChange={(e) => setEventData(prev => ({ ...prev, location_url: e.target.value }))}
                      placeholder="https://maps.google.com/..."
                      className="bg-zinc-800 border-zinc-700 mt-1"
                    />
                  </div>
                </div>
              </section>

              {/* Event Visibility */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Tag size={20} className="text-violet-500" />
                  Visibilidad del Evento
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {VISIBILITY_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setEventData(prev => ({ ...prev, visibility: type.value }))}
                      className={`p-4 rounded-xl border text-left transition-colors ${
                        eventData.visibility === type.value
                          ? 'bg-violet-500/20 border-violet-500 text-white'
                          : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                      }`}
                    >
                      <p className="font-bold">{type.label}</p>
                      <p className="text-xs mt-1 opacity-70">{type.description}</p>
                    </button>
                  ))}
                </div>
                
                <p className="text-xs text-zinc-500 mt-3">
                  Los eventos privados solo son accesibles mediante el link directo que puedes compartir.
                </p>
              </section>

              {/* Age Restriction */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Shield size={20} className="text-violet-500" />
                  Restricción de Edad
                </h2>
                
                <div className="space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={eventData.age_restriction_enabled}
                      onChange={(e) => setEventData(prev => ({ 
                        ...prev, 
                        age_restriction_enabled: e.target.checked 
                      }))}
                      className="w-5 h-5 rounded border-zinc-700 bg-zinc-800 text-violet-500 focus:ring-violet-500"
                    />
                    <span className="text-zinc-300">Habilitar restricción de edad</span>
                  </label>
                  
                  {eventData.age_restriction_enabled && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="flex items-center gap-4 pl-8"
                    >
                      <Label className="text-zinc-400 whitespace-nowrap">Edad mínima:</Label>
                      <Input
                        type="number"
                        value={eventData.minimum_age}
                        onChange={(e) => setEventData(prev => ({ 
                          ...prev, 
                          minimum_age: parseInt(e.target.value) || 18 
                        }))}
                        min={1}
                        max={99}
                        className="bg-zinc-800 border-zinc-700 w-24"
                      />
                      <span className="text-zinc-500">años</span>
                    </motion.div>
                  )}
                  
                  <p className="text-xs text-zinc-500 pl-8">
                    Si activas esta opción, solo podrán comprar entradas personas mayores a la edad indicada.
                  </p>
                </div>
              </section>

              {/* Tickets per Purchase Limit */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Ticket size={20} className="text-violet-500" />
                  Máximo de entradas por compra
                </h2>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <Input
                      type="number"
                      value={eventData.max_orders_per_user || ''}
                      onChange={(e) => setEventData(prev => ({ 
                        ...prev, 
                        max_orders_per_user: e.target.value ? parseInt(e.target.value) : null 
                      }))}
                      placeholder="Sin límite"
                      min={1}
                      max={20}
                      className="bg-zinc-800 border-zinc-700 w-32"
                      data-testid="max-tickets-per-purchase"
                    />
                    <span className="text-zinc-400">entrada(s) máximo por compra</span>
                  </div>
                  
                  <p className="text-xs text-zinc-500">
                    Limita cuántas entradas puede comprar una persona en <strong>una sola transacción</strong>. Ejemplo: si ponés 4, no se podrán comprar más de 4 tickets juntos. Dejá vacío para no aplicar límite.
                  </p>
                </div>
              </section>

              {/* Custom Terms & Conditions */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h2 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                  <FileText size={20} className="text-amber-500" />
                  Términos y condiciones del evento
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-normal bg-zinc-800 border border-zinc-700 rounded-full px-2 py-0.5 ml-1">Opcional</span>
                </h2>
                <p className="text-xs text-amber-400/80 mb-4 flex items-start gap-1.5">
                  <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                  <span>Una vez creados se <strong>bloquean</strong>. Solo el equipo de Bravo podrá modificarlos después por seguridad legal.</span>
                </p>
                <textarea
                  value={eventData.custom_terms || ''}
                  onChange={(e) => setEventData(prev => ({ ...prev, custom_terms: e.target.value }))}
                  rows={6}
                  placeholder="Ej: Por motivos de seguridad náutica, el evento se realiza en una embarcación. Los asistentes declaran saber nadar, no padecer condiciones de salud que les impidan navegar, y aceptan las indicaciones del capitán. La empresa no se responsabiliza por objetos personales..."
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm leading-relaxed"
                  data-testid="custom-terms"
                />
                <p className="text-xs text-zinc-500 mt-2">
                  Dejá vacío si no necesitás términos específicos. Estos se mostrarán al comprador antes del checkout.
                </p>
                
                {/* Toggle: include T&C in confirmation email */}
                <label className={`mt-4 flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${eventData.include_terms_in_email ? 'border-violet-500/50 bg-violet-500/5' : 'border-zinc-800 bg-zinc-900/40'} ${!eventData.custom_terms?.trim() ? 'opacity-50 cursor-not-allowed' : 'hover:border-zinc-700'}`}>
                  <input
                    type="checkbox"
                    checked={!!eventData.include_terms_in_email}
                    onChange={(e) => setEventData(prev => ({ ...prev, include_terms_in_email: e.target.checked }))}
                    disabled={!eventData.custom_terms?.trim()}
                    className="mt-0.5 accent-violet-500"
                    data-testid="include-terms-in-email"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">📧 Incluir los T&C en el email de confirmación</p>
                    <p className="text-[11px] text-zinc-400 mt-0.5">Cada comprador recibirá los términos en el cuerpo del mail junto al QR. Útil como prueba de aceptación ante disputas con Mercado Pago.</p>
                  </div>
                </label>
              </section>

              {/* Date & Time */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h2 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                  <Clock size={20} className="text-violet-500" />
                  ¿Cuándo es el evento?
                </h2>
                <p className="text-xs text-zinc-500 mb-5">El horario se interpreta en tu zona horaria local.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Start */}
                  <div className="rounded-xl bg-gradient-to-br from-violet-500/10 to-violet-500/5 border border-violet-500/25 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-xs uppercase tracking-wider text-violet-300 font-bold flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-violet-400" />
                        Empieza
                      </Label>
                      <span className="text-[10px] uppercase tracking-wider text-violet-400/70">Obligatorio</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2">
                        <Input
                          type="date"
                          value={eventData.start_date}
                          min={new Date().toISOString().slice(0, 10)}
                          max={`${new Date().getFullYear() + 3}-12-31`}
                          onChange={(e) => setEventData(prev => ({ ...prev, start_date: e.target.value }))}
                          className="bg-zinc-900 border-zinc-700 text-white"
                          data-testid="event-start-date"
                        />
                      </div>
                      <div>
                        <Input
                          type="time"
                          value={eventData.start_time}
                          onChange={(e) => setEventData(prev => ({ ...prev, start_time: e.target.value }))}
                          className="bg-zinc-900 border-zinc-700 text-white text-center font-mono"
                          data-testid="event-start-time"
                        />
                      </div>
                    </div>
                    {eventData.start_date && eventData.start_time && (
                      <div className="mt-3 px-3 py-2 rounded-lg bg-black/30 border border-violet-500/15">
                        <div className="text-[10px] text-violet-300/70 uppercase tracking-wider mb-0.5">Se publicará como</div>
                        <div className="text-sm text-white font-medium capitalize">
                          {formatArgentinaLong(localDateAndTimeToISO(eventData.start_date, eventData.start_time))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* End */}
                  <div className="rounded-xl bg-gradient-to-br from-zinc-800/40 to-zinc-800/20 border border-zinc-700 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-xs uppercase tracking-wider text-zinc-300 font-bold flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-zinc-500" />
                        Termina
                      </Label>
                      <span className="text-[10px] uppercase tracking-wider text-zinc-500">Opcional</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2">
                        <Input
                          type="date"
                          value={eventData.end_date}
                          min={eventData.start_date || new Date().toISOString().slice(0, 10)}
                          max={`${new Date().getFullYear() + 3}-12-31`}
                          onChange={(e) => setEventData(prev => ({ ...prev, end_date: e.target.value }))}
                          className="bg-zinc-900 border-zinc-700 text-white"
                          data-testid="event-end-date"
                        />
                      </div>
                      <div>
                        <Input
                          type="time"
                          value={eventData.end_time}
                          onChange={(e) => setEventData(prev => ({ ...prev, end_time: e.target.value }))}
                          className="bg-zinc-900 border-zinc-700 text-white text-center font-mono"
                          data-testid="event-end-time"
                        />
                      </div>
                    </div>
                    {eventData.end_date && eventData.end_time ? (
                      <div className="mt-3 px-3 py-2 rounded-lg bg-black/30 border border-zinc-700">
                        <div className="text-[10px] text-zinc-400 uppercase tracking-wider mb-0.5">Cierre</div>
                        <div className="text-sm text-white font-medium capitalize">
                          {formatArgentinaLong(localDateAndTimeToISO(eventData.end_date, eventData.end_time))}
                        </div>
                      </div>
                    ) : (
                      <p className="mt-3 text-[11px] text-zinc-500 leading-snug">Dejalo vacío si no querés mostrar hora de cierre.</p>
                    )}
                  </div>
                </div>

                {/* Quick presets */}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-1">Atajos:</span>
                  {[
                    { label: 'Viernes 23 hs',  weekday: 5, time: '23:00' },
                    { label: 'Sábado 23 hs',   weekday: 6, time: '23:00' },
                    { label: 'Sábado 16 hs',   weekday: 6, time: '16:00' },
                    { label: 'Domingo 17 hs',  weekday: 0, time: '17:00' },
                  ].map(p => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => {
                        // Find next occurrence of the target weekday
                        const today = new Date();
                        const target = new Date(today);
                        const diff = (p.weekday - today.getDay() + 7) % 7 || 7;
                        target.setDate(today.getDate() + diff);
                        const yyyy = target.getFullYear();
                        const mm = String(target.getMonth() + 1).padStart(2, '0');
                        const dd = String(target.getDate()).padStart(2, '0');
                        setEventData(prev => ({ ...prev, start_date: `${yyyy}-${mm}-${dd}`, start_time: p.time }));
                      }}
                      className="px-2.5 py-1 rounded-full text-[11px] bg-zinc-800 hover:bg-violet-500/15 text-zinc-300 hover:text-violet-200 border border-zinc-700 hover:border-violet-500/40 transition-all"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </section>

              {/* Image */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <ImageIcon size={20} className="text-violet-500" />
                  Flyer del Evento
                </h2>
                
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="w-full md:w-64 aspect-[3/4] bg-zinc-800 rounded-xl overflow-hidden relative group">
                    {eventData.image_url ? (
                      <img 
                        src={eventData.image_url} 
                        alt="Flyer" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-zinc-600">
                        <ImageIcon size={48} className="mb-2" />
                        <p className="text-sm">Vista previa</p>
                      </div>
                    )}
                    <label className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                      {uploading ? (
                        <Loader2 className="animate-spin text-white" size={32} />
                      ) : (
                        <Upload className="text-white" size={32} />
                      )}
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploading}
                      />
                    </label>
                  </div>
                  
                  <div className="flex-1">
                    <div className="bg-zinc-800/50 rounded-lg p-4 text-sm text-zinc-400">
                      <p className="font-medium text-white mb-2">Recomendaciones:</p>
                      <ul className="space-y-1 list-disc list-inside">
                        <li>Formato: JPG o PNG</li>
                        <li>Tamaño recomendado: 1080 x 1350 px (4:5)</li>
                        <li>Peso máximo: 10MB</li>
                        <li>Usa imágenes de alta calidad</li>
                      </ul>
                    </div>
                    
                    <label className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-zinc-800 rounded-lg cursor-pointer hover:bg-zinc-700 transition-colors">
                      <Upload size={18} />
                      <span>Subir imagen</span>
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploading}
                      />
                    </label>
                  </div>
                </div>
              </section>

              {/* YouTube Video */}
              {/* Next Button */}
              <div className="flex justify-end">
                <Button
                  onClick={handleNext}
                  className="bg-gradient-to-r from-violet-600 to-pink-600 text-white px-8"
                >
                  Continuar
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Venue Selection */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Map size={20} className="text-violet-500" />
                  Mapa Interactivo del Venue
                </h2>
                
                <p className="text-zinc-400 mb-6">
                  Vincula un mapa de venue para que los compradores puedan ver las zonas disponibles al comprar sus entradas.
                  Esto es opcional - si no tienes un venue creado o prefieres no usarlo, puedes omitir este paso.
                </p>
                
                <VenueSelector
                  selectedVenueId={eventData.venue_id}
                  onSelect={(venueId) => {
                    setEventData(prev => ({ ...prev, venue_id: venueId || '' }));
                    if (!venueId) setSelectedVenue(null);
                  }}
                  onVenueLoaded={(venue) => setSelectedVenue(venue)}
                />
              </section>
              
              {/* Navigation */}
              <div className="flex justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  className="border-zinc-700 text-zinc-400"
                >
                  <ArrowLeft size={18} className="mr-2" />
                  Volver
                </Button>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={handleSkipVenue}
                    className="border-zinc-700 text-zinc-400"
                  >
                    Omitir
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                  
                  {eventData.venue_id && (
                    <Button
                      onClick={handleNext}
                      className="bg-gradient-to-r from-violet-600 to-pink-600 text-white px-8"
                    >
                      Continuar con Venue
                      <ArrowRight size={18} className="ml-2" />
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Tickets */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {/* Use VenueTicketEditor if venue is selected */}
              {selectedVenue ? (
                <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                  <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Ticket size={20} className="text-violet-500" />
                    Configurar Entradas - {selectedVenue.name}
                  </h2>
                  <VenueTicketEditor
                    venue={selectedVenue}
                    tickets={tickets}
                    setTickets={setTickets}
                    onAddTicket={addTicket}
                    onRemoveTicket={removeTicket}
                    onUpdateTicket={updateTicket}
                  />
                </section>
              ) : (
                /* Original tickets form when no venue selected */
                <>
                  {tickets.map((ticket, index) => (
                    <section 
                      key={ticket.id}
                      className="bg-zinc-900 border border-zinc-800 rounded-xl p-6"
                    >
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-white flex items-center gap-2">
                      <Ticket size={20} className="text-violet-500" />
                      Entrada {index + 1}
                    </h2>
                    {tickets.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTicket(ticket.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-400">Nombre del ticket *</Label>
                      <Input
                        value={ticket.name}
                        onChange={(e) => updateTicket(ticket.id, 'name', e.target.value)}
                        placeholder="Ej: Early Bird, VIP, General"
                        className="bg-zinc-800 border-zinc-700 mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-zinc-400">Tipo de entrada</Label>
                      <select
                        value={ticket.type}
                        onChange={(e) => updateTicket(ticket.id, 'type', e.target.value)}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-2.5 text-white mt-1"
                      >
                        {TICKET_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Venue Section Linking */}
                    {selectedVenue && ((selectedVenue.sections && selectedVenue.sections.length > 0) || (selectedVenue.elements && selectedVenue.elements.length > 0)) && (
                      <div className="md:col-span-2">
                        <Label className="text-zinc-400 flex items-center gap-2">
                          <Layers size={14} />
                          Vincular con sección del venue (opcional)
                        </Label>
                        <div className="mt-1 flex gap-2">
                          <select
                            value={ticket.linked_section_id || ''}
                            onChange={(e) => {
                              const sectionId = e.target.value;
                              // Support both 'sections' (API response) and 'elements' (legacy)
                              const allItems = selectedVenue.sections || selectedVenue.elements || [];
                              const section = allItems.find(el => el.id === sectionId);
                              updateTicket(ticket.id, 'linked_section_id', sectionId);
                              updateTicket(ticket.id, 'linked_section_name', section?.name || '');
                            }}
                            className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg p-2.5 text-white"
                          >
                            <option value="">Sin vincular</option>
                            {(selectedVenue.sections || selectedVenue.elements || [])
                              .filter(el => 
                                el.name && 
                                el.type !== 'wall' && 
                                el.type !== 'corner' && 
                                el.type !== 'pillar' &&
                                el.type !== 'bathroom' &&
                                el.type !== 'exit' &&
                                el.type !== 'stairs'
                              )
                              .map(section => (
                                <option key={section.id} value={section.id}>
                                  {section.name} {section.capacity > 0 ? `(${section.capacity} cap.)` : ''} - {section.type}
                                </option>
                              ))
                            }
                          </select>
                          {ticket.linked_section_id && (
                            <div 
                              className="w-10 h-10 rounded-lg flex-shrink-0"
                              style={{ 
                                backgroundColor: (selectedVenue.sections || selectedVenue.elements || []).find(el => el.id === ticket.linked_section_id)?.color || '#4F46E5',
                                opacity: 0.8
                              }}
                              title={ticket.linked_section_name}
                            />
                          )}
                        </div>
                        <p className="text-[10px] text-zinc-600 mt-1">
                          Vincular permite que los compradores vean esta entrada en el mapa del venue
                        </p>
                      </div>
                    )}
                    
                    <div>
                      <Label className="text-zinc-400">Stock *</Label>
                      <Input
                        type="number"
                        value={ticket.stock}
                        onChange={(e) => updateTicket(ticket.id, 'stock', parseInt(e.target.value) || 0)}
                        min={1}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-zinc-400">Precio ($)</Label>
                      <Input
                        type="number"
                        value={ticket.price}
                        onChange={(e) => updateTicket(ticket.id, 'price', parseFloat(e.target.value) || 0)}
                        min={0}
                        step={100}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                        disabled={ticket.tier_type === 'inquiry' || (ticket.price_stages && ticket.price_stages.length > 0)}
                      />
                      {ticket.price_stages && ticket.price_stages.length > 0 && (
                        <p className="text-[10px] text-violet-400 mt-1">Usando precio por etapas ↓</p>
                      )}
                      {ticket.tier_type === 'inquiry' && (
                        <p className="text-[10px] text-cyan-400 mt-1">Sin precio — se redirige al link de consulta</p>
                      )}
                    </div>

                    {/* === Tier Type & Multi-stage controls === */}
                    <div className="md:col-span-2 space-y-3">
                      <div>
                        <Label className="text-zinc-400 mb-2 block">Tipo de tanda</Label>
                        <div className="grid grid-cols-2 gap-2" data-testid={`tier-type-picker-${ticket.id}`}>
                          {[
                            { id: 'sale', label: 'Venta online', desc: 'Compra directa con MP', icon: '🛒' },
                            { id: 'inquiry', label: 'Consultar', desc: 'Redirige a WhatsApp/DM', icon: '💬' },
                          ].map(opt => (
                            <button
                              key={opt.id}
                              type="button"
                              data-testid={`tier-type-${opt.id}-${ticket.id}`}
                              onClick={() => updateTicket(ticket.id, 'tier_type', opt.id)}
                              className={`p-3 rounded-lg border text-left transition-all ${ticket.tier_type === opt.id ? 'border-violet-500 bg-violet-500/10' : 'border-zinc-700 hover:border-zinc-600'}`}
                            >
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-lg">{opt.icon}</span>
                                <span className="text-sm text-white font-medium">{opt.label}</span>
                              </div>
                              <p className="text-[10px] text-zinc-500">{opt.desc}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Inquiry fields */}
                      {ticket.tier_type === 'inquiry' && (
                        <div className="space-y-2 p-3 rounded-lg border border-cyan-500/30 bg-cyan-500/5">
                          <p className="text-xs text-cyan-200">
                            Esta opción no se vende online. Al elegirla, el comprador será redirigido al link que pegues abajo.
                          </p>
                          <div>
                            <Label className="text-zinc-400 text-xs">Link de consulta *</Label>
                            <Input
                              value={ticket.inquiry_link}
                              onChange={(e) => updateTicket(ticket.id, 'inquiry_link', e.target.value)}
                              placeholder="https://wa.me/541144443333?text=Hola, consulto por..."
                              className="bg-zinc-800 border-zinc-700 mt-1"
                              data-testid={`inquiry-link-${ticket.id}`}
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-xs">Texto del botón (opcional)</Label>
                            <Input
                              value={ticket.inquiry_label}
                              onChange={(e) => updateTicket(ticket.id, 'inquiry_label', e.target.value)}
                              placeholder="Ej: Consultar por WhatsApp"
                              className="bg-zinc-800 border-zinc-700 mt-1"
                            />
                          </div>
                        </div>
                      )}

                      {/* Multi-stage Pricing */}
                      {ticket.tier_type === 'sale' && (
                        <div className="p-3 rounded-lg border border-violet-500/30 bg-violet-500/5 space-y-2" data-testid={`price-stages-block-${ticket.id}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm text-violet-200 font-medium flex items-center gap-1.5">
                                🎯 Precios por etapas (tandas)
                                <span className="text-[9px] uppercase tracking-wider text-violet-400 bg-violet-500/15 border border-violet-500/30 rounded-full px-1.5 py-0.5">Opcional</span>
                              </div>
                              <p className="text-[10px] text-zinc-400 mt-0.5 leading-snug">Cuando se agota una tanda, se activa la siguiente automáticamente.</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const next = [...(ticket.price_stages || []), { label: `Tanda ${(ticket.price_stages?.length || 0) + 1}`, price: ticket.price || 0, quantity: 0, starts_at: '', ends_at: '' }];
                                updateTicket(ticket.id, 'price_stages', next);
                              }}
                              className="text-xs px-2 py-1 rounded-md bg-violet-500 hover:bg-violet-600 text-white font-medium"
                              data-testid={`add-stage-btn-${ticket.id}`}
                            >
                              + Tanda
                            </button>
                          </div>
                          {(ticket.price_stages || []).map((stage, idx) => (
                            <div key={idx} className="rounded-md border border-zinc-700 bg-zinc-900/60 p-2 space-y-1.5">
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] uppercase text-violet-300 font-bold bg-violet-500/15 rounded px-1.5 py-0.5">#{idx + 1}</span>
                                <Input
                                  value={stage.label || ''}
                                  onChange={(e) => {
                                    const stages = [...ticket.price_stages];
                                    stages[idx] = { ...stages[idx], label: e.target.value };
                                    updateTicket(ticket.id, 'price_stages', stages);
                                  }}
                                  placeholder="Ej: Preventa, 2da Tanda, Última"
                                  className="bg-zinc-800 border-zinc-700 h-7 text-xs flex-1"
                                />
                                <button
                                  type="button"
                                  onClick={() => updateTicket(ticket.id, 'price_stages', ticket.price_stages.filter((_, i) => i !== idx))}
                                  className="text-red-400 hover:text-red-300 text-xs px-1"
                                >✕</button>
                              </div>
                              <div className="grid grid-cols-2 gap-1.5">
                                <Input
                                  type="number"
                                  value={stage.price || 0}
                                  onChange={(e) => {
                                    const stages = [...ticket.price_stages];
                                    stages[idx] = { ...stages[idx], price: parseFloat(e.target.value) || 0 };
                                    updateTicket(ticket.id, 'price_stages', stages);
                                  }}
                                  placeholder="Precio $"
                                  className="bg-zinc-800 border-zinc-700 h-7 text-xs"
                                />
                                <Input
                                  type="number"
                                  value={stage.quantity || 0}
                                  onChange={(e) => {
                                    const stages = [...ticket.price_stages];
                                    stages[idx] = { ...stages[idx], quantity: parseInt(e.target.value) || 0 };
                                    updateTicket(ticket.id, 'price_stages', stages);
                                  }}
                                  placeholder="Cantidad"
                                  className="bg-zinc-800 border-zinc-700 h-7 text-xs"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-1.5">
                                <Input
                                  type="datetime-local"
                                  value={stage.starts_at ? stage.starts_at.slice(0, 16) : ''}
                                  onChange={(e) => {
                                    const stages = [...ticket.price_stages];
                                    stages[idx] = { ...stages[idx], starts_at: e.target.value ? new Date(e.target.value).toISOString() : '' };
                                    updateTicket(ticket.id, 'price_stages', stages);
                                  }}
                                  className="bg-zinc-800 border-zinc-700 h-7 text-xs"
                                  title="Inicio (opcional)"
                                />
                                <Input
                                  type="datetime-local"
                                  value={stage.ends_at ? stage.ends_at.slice(0, 16) : ''}
                                  onChange={(e) => {
                                    const stages = [...ticket.price_stages];
                                    stages[idx] = { ...stages[idx], ends_at: e.target.value ? new Date(e.target.value).toISOString() : '' };
                                    updateTicket(ticket.id, 'price_stages', stages);
                                  }}
                                  className="bg-zinc-800 border-zinc-700 h-7 text-xs"
                                  title="Fin (opcional)"
                                />
                              </div>
                            </div>
                          ))}
                          {(ticket.price_stages?.length || 0) === 0 && (
                            <p className="text-[10px] text-zinc-500 italic text-center py-1.5">Sin tandas — se usa el precio único de arriba.</p>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <Label className="text-zinc-400">Máximo por orden</Label>
                      <Input
                        type="number"
                        value={ticket.max_per_order}
                        onChange={(e) => updateTicket(ticket.id, 'max_per_order', parseInt(e.target.value) || 1)}
                        min={1}
                        max={10}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-zinc-400">Descripción (opcional)</Label>
                      <Input
                        value={ticket.description}
                        onChange={(e) => updateTicket(ticket.id, 'description', e.target.value)}
                        placeholder="Beneficios incluidos..."
                        className="bg-zinc-800 border-zinc-700 mt-1"
                      />
                    </div>
                    
                    {/* Advanced Options */}
                    <div className="md:col-span-2 border-t border-zinc-800 pt-4 mt-2">
                      <p className="text-sm text-zinc-500 mb-3">Opciones avanzadas</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-zinc-400">Fin de venta (opcional)</Label>
                          <Input
                            type="datetime-local"
                            value={ticket.sale_end_date}
                            onChange={(e) => updateTicket(ticket.id, 'sale_end_date', e.target.value)}
                            className="bg-zinc-800 border-zinc-700 mt-1"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-zinc-400">Límite horario ingreso (opcional)</Label>
                          <Input
                            type="time"
                            value={ticket.entry_limit_time}
                            onChange={(e) => updateTicket(ticket.id, 'entry_limit_time', e.target.value)}
                            className="bg-zinc-800 border-zinc-700 mt-1"
                          />
                        </div>
                        
                        <div className="flex items-center gap-6">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={ticket.is_visible}
                              onChange={(e) => updateTicket(ticket.id, 'is_visible', e.target.checked)}
                              className="rounded border-zinc-700"
                            />
                            <span className="text-sm text-zinc-400">Visible en página</span>
                          </label>
                          
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={ticket.is_active}
                              onChange={(e) => updateTicket(ticket.id, 'is_active', e.target.checked)}
                              className="rounded border-zinc-700"
                            />
                            <span className="text-sm text-zinc-400">Activo</span>
                          </label>
                        </div>
                        
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Label className="text-zinc-400">Código descuento</Label>
                            <Input
                              value={ticket.discount_code}
                              onChange={(e) => updateTicket(ticket.id, 'discount_code', e.target.value.toUpperCase())}
                              placeholder="PROMO20"
                              className="bg-zinc-800 border-zinc-700 mt-1 font-mono"
                            />
                          </div>
                          <div className="w-24">
                            <Label className="text-zinc-400">% Desc.</Label>
                            <Input
                              type="number"
                              value={ticket.discount_percent}
                              onChange={(e) => updateTicket(ticket.id, 'discount_percent', parseInt(e.target.value) || 0)}
                              min={0}
                              max={100}
                              className="bg-zinc-800 border-zinc-700 mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              ))}

              {/* Add Ticket Button */}
              <button
                type="button"
                onClick={addTicket}
                className="w-full p-4 border-2 border-dashed border-zinc-700 rounded-xl text-zinc-500 hover:text-white hover:border-zinc-500 transition-colors flex items-center justify-center gap-2"
              >
                <Plus size={20} />
                Agregar otra entrada
              </button>
                </>
              )}

              {/* Terms and Conditions */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <FileText size={20} className="text-violet-500" />
                  Términos y Condiciones
                </h2>
                
                <div className="space-y-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="w-5 h-5 mt-0.5 rounded border-zinc-700 bg-zinc-800 text-violet-500 focus:ring-violet-500"
                    />
                    <span className="text-zinc-300">
                      He leído y acepto los{' '}
                      <button
                        type="button"
                        onClick={() => setShowTermsModal(true)}
                        className="text-violet-500 hover:underline"
                      >
                        Términos y Condiciones para Productoras
                      </button>
                    </span>
                  </label>
                </div>
              </section>

              {/* Info Banner for non-verified */}
              {!producer?.is_verified && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-start gap-3">
                  <AlertCircle className="text-yellow-500 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="text-yellow-400 font-medium">Productora en proceso de verificación</p>
                    <p className="text-yellow-400/70 text-sm mt-1">
                      Tu evento será revisado por nuestro equipo antes de publicarse. Esto suele tomar entre 24-48 horas hábiles.
                    </p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <Button
                  onClick={handleBack}
                  variant="outline"
                  className="border-zinc-700"
                >
                  <ArrowLeft size={18} className="mr-2" />
                  Volver
                </Button>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Draft Button - Always available */}
                  <Button
                    onClick={() => handleSubmit('draft')}
                    disabled={saving}
                    variant="outline"
                    className="border-zinc-700 hover:bg-zinc-800"
                  >
                    {saving ? (
                      <Loader2 className="animate-spin mr-2" size={18} />
                    ) : (
                      <FileText size={18} className="mr-2" />
                    )}
                    Guardar Borrador
                  </Button>
                  
                  {/* Main Action Button - Depends on verification status */}
                  {producer?.is_verified ? (
                    // Verified: Can publish directly
                    <Button
                      onClick={() => handleSubmit('publish')}
                      disabled={saving || !termsAccepted}
                      className="bg-green-600 hover:bg-green-700 text-white px-8 disabled:opacity-50"
                    >
                      {saving ? (
                        <Loader2 className="animate-spin mr-2" size={18} />
                      ) : (
                        <CheckCircle2 size={18} className="mr-2" />
                      )}
                      Publicar Evento
                    </Button>
                  ) : (
                    // Not verified: Must submit for approval
                    <Button
                      onClick={() => handleSubmit('submit_for_approval')}
                      disabled={saving || !termsAccepted}
                      className="bg-gradient-to-r from-violet-600 to-pink-600 text-white px-8 disabled:opacity-50"
                    >
                      {saving ? (
                        <Loader2 className="animate-spin mr-2" size={18} />
                      ) : (
                        <Clock size={18} className="mr-2" />
                      )}
                      Solicitar Aprobación
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Terms Modal */}
        {showTermsModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
            >
              <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">
                  {producerTerms?.title || 'Términos y Condiciones'}
                </h3>
                <button
                  onClick={() => setShowTermsModal(false)}
                  className="text-zinc-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh] prose prose-invert prose-sm">
                <div 
                  className="text-zinc-300 whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ 
                    __html: producerTerms?.content?.replace(/\n/g, '<br/>') || 'Cargando...' 
                  }} 
                />
              </div>
              <div className="p-4 border-t border-zinc-800 flex justify-end">
                <Button
                  onClick={() => {
                    setTermsAccepted(true);
                    setShowTermsModal(false);
                  }}
                  className="bg-gradient-to-r from-violet-600 to-pink-600 text-white"
                >
                  Aceptar términos
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </ProducerLayout>
  );
};

export default CreateEventPage;
